<template>
  <div class="product-page">
    <div
      v-if="hasFetchedData"
      class="product-page__content"
      :class="{ 'product-page__content--visible': isVisible }"
    >
      <SeoProduct
        :sku="currentSpreeProduct.sku"
        :name="product.name"
        :description="product.description"
        :images="productImages"
        :ratingValue="reviewRatingAverageOverall"
        :ratingCount="reviewTotalOverall"
        :url="productUrl"
        :currency="currency.code"
        :price="currentSpreeProduct.price"
        :inStock="hasProductInStock"
      />
      <keep-alive>
        <TopBanner
          v-if="isRaffle && !hasRaffleCountdownEnded && !isGalleryOpen"
          :imgUrlDesktop="topBannerImageUrlDesktop"
          :imgUrlMobile="topBannerImageUrlMobile"
          :videoUrlDesktop="topBannerVideoUrlDesktop"
          :videoUrlMobile="topBannerVideoUrlMobile"
        >
          <TopBannerCountdown :title="product.name" :endDate="raffleExpiresAt"></TopBannerCountdown
        ></TopBanner>
      </keep-alive>

      <TopContainer />
      <transition name="fade">
        <div
          v-show="!isGalleryOpen && hasLazyComponentsMounted"
          class="product-page__bottom-container"
        >
          <ProductMediaGallery
            v-if="
              hasLazyComponentsMounted &&
              mediaGalleryPosition === MEDIA_GALLERY_POSITIONS.belowPurchasePod
            "
            :media="mediaGalleryItems"
            :campaignIds="mediaGalleryPositionCampaignIds"
          />
          <Recommendations
            :selectorName="DY_API_CAMPAIGN_SELECTOR_NAMES.pdpProductRecommendationsTop"
          />
          <Highlights v-if="hasLazyComponentsMounted" />
          <!-- MediaGallery & ImageContentful need to be visible in order to render images correctly -->
          <Specs
            v-if="shouldShowSpecs"
            ref="specs"
            :isIframeLayout="isIframeLayout"
            :isComparisonTool="isComparisonTool"
            :productType="currentVariant.spreeProduct.productType"
            :selectedGender="selectedGender"
            :slugs.sync="specsSlugs"
            :theme="isMqLarge ? SPECS_THEMES.dark : SPECS_THEMES.white"
            @hook:mounted="onSpecsMounted"
          />
          <ProductMediaGallery
            v-if="
              hasLazyComponentsMounted &&
              mediaGalleryPosition === MEDIA_GALLERY_POSITIONS.nearBottom
            "
            :media="mediaGalleryItems"
            :campaignIds="mediaGalleryPositionCampaignIds"
          />
          <Recommendations
            v-if="!isGiftCard"
            :selectorName="DY_API_CAMPAIGN_SELECTOR_NAMES.pdpProductRecommendationsBottom"
          />
          <Reviews v-if="!isRaffle && !isGiftCard" />
        </div>
      </transition>
    </div>
    <LoadingIndicator v-if="!isVisible" class="product-page__loader" />
  </div>
</template>

<script>
import 'url-search-params-polyfill'
import { xor } from 'lodash-es'
import { mapState, mapGetters, mapMutations, mapActions } from 'vuex'
import DynamicYieldService from '@/api/services/_shared/dynamic-yield-service'
import { DY_PAGE_TYPES } from '@/services/external-communication/dynamic-yield/dy-events'
import { DY_API_CAMPAIGN_SELECTOR_NAMES, MEDIA_GALLERY_POSITIONS } from '@/config/constants'
import { waitFor } from '@/helpers/time'
import { isEmpty } from '@/helpers/object'
import { registerStores } from '@/helpers/stores'
import { disableZoom, enableZoom } from '@/helpers/disable-zoom'
import { isIPhone } from '@/helpers/browser-support'
import { addScriptToDOM, isScriptAddedToDOM } from '@/helpers/script'
import { toRetry } from '@/helpers/request'
import flash from '@/mixins/flash'
import filterStore from '@/stores/filter'
import productStore, { PRODUCT_TYPES, GENDERS } from '@/stores/product'
import checkoutStore from '@/stores/checkout'
import LoadingIndicator from '@/components/_shared/LoadingIndicator.vue'
import { preloadStickyMenuComponent } from '@/components/_shared/StickyMenu/StickyMenu.vue'
import { SPECS_THEMES } from '@/components/_shared/Specs/constants'
import SeoProduct from '@/components/ProductPage/SeoProduct.vue'
import TopContainer from '@/components/ProductPage/TopContainer.vue'
import Highlights from '@/components/ProductPage/Highlights/Highlights.vue'
import ProductMediaGallery from '@/components/ProductPage/ProductMediaGallery/ProductMediaGallery.vue'
import Reviews from '@/components/ProductPage/Reviews/Reviews.vue'
import Recommendations from '@/components/ProductPage/Recommendations.vue'

export default {
  name: 'ProductPage',

  components: {
    SeoProduct,
    TopContainer,
    Highlights,
    ProductMediaGallery,
    Reviews,
    Recommendations,
    LoadingIndicator,
    // Imported dynamically to improve webpack bundle sizes
    Specs: () => import(/* webpackChunkName: "specs" */ '@/components/_shared/Specs/Specs.vue'),
    TopBanner: () =>
      import(
        /* webpackChunkName: "top-banner" */ '@/components/ProductPage/TopBanner/TopBanner.vue'
      ),
    TopBannerCountdown: () =>
      import(
        /* webpackChunkName: "top-banner-countdown" */ '@/components/ProductPage/TopBanner/TopBannerCountdown.vue'
      ),
  },

  mixins: [flash],

  data() {
    return {
      isComparisonToolOnDesktop: true,
      isIframeLayout: false,
      isIPhone,
      isSpecsMounted: false,
      shouldShowSpecs: false,
      specsSlugs: [],
      SPECS_THEMES,
      MEDIA_GALLERY_POSITIONS,
      mediaGalleryPositionCampaignIds: undefined,
      PRODUCT_TYPES,
      DY_API_CAMPAIGN_SELECTOR_NAMES,
    }
  },

  computed: {
    ...mapState('product', ['isLoaded', 'selectedGender', 'isGalleryOpen', 'mediaGalleryPosition']),
    ...mapState('store', ['hasFetchedStoreData', 'currency']),
    ...mapState('page', ['country']),

    ...mapGetters({
      currentSpreeProduct: 'product/currentSpreeProduct',
      currentVariant: 'product/currentVariant',
      firstFemaleVariantId: 'product/firstFemaleVariantId',
      firstMaleVariantId: 'product/firstMaleVariantId',
      hasLazyComponentsMounted: 'product/hasLazyComponentsMounted',
      isVisible: 'product/isVisible',
      product: 'product/product',
      productGroupSlug: 'product/productGroupSlug',
      reviewTotalOverall: 'product/reviewTotalOverall',
      reviewRatingAverageOverall: 'product/reviewRatingAverageOverall',
      isRaffle: 'product/isRaffle',
      isGiftCard: 'product/isGiftCard',
      topBannerImageUrlMobile: 'product/topBannerImageUrlMobile',
      topBannerImageUrlDesktop: 'product/topBannerImageUrlDesktop',
      topBannerVideoUrlMobile: 'product/topBannerVideoUrlMobile',
      topBannerVideoUrlDesktop: 'product/topBannerVideoUrlDesktop',
      raffleExpiresAt: 'product/raffleExpiresAt',
      hasRaffleCountdownEnded: 'product/hasRaffleCountdownEnded',
      isMqLarge: 'viewport/isMqLarge',
      currency: 'store/currency',
      isCookieFunctionalityAccepted: 'cookieConsent/isCookieFunctionalityAccepted',
    }),

    hasFetchedData() {
      return this.isLoaded && this.hasFetchedStoreData
    },

    productImages() {
      const images = []
      this.product.variants.forEach(variant => {
        images.push(
          ...variant.assets.map(asset => this.addUrlProtocol(asset.mediaUrl)),
          ...variant.highlights.map(highlight => this.addUrlProtocol(highlight.imageUrl))
        )
      })
      return images
    },

    mediaGalleryItems() {
      return this.product.mediaGalleryItems
    },

    productUrl() {
      return this.currentSpreeProduct.productUrl.replace(/\/[a-z]{2}-[a-z]{2}\//, '/')
    },

    isShoe() {
      return this.product.variants[0].spreeProduct.productType === PRODUCT_TYPES.shoes
    },

    isComparisonTool() {
      return this.isShoe && this.isComparisonToolOnDesktop
    },

    hasProductInStock() {
      return this.currentVariant.spreeProduct.spreeVariants.some(
        spreeVariant => spreeVariant.stock > 0
      )
    },

    gender() {
      if (this.currentVariant.gender === GENDERS.unisex) return GENDERS.unisex
      return this.$route.params.gender === GENDERS.womens || !this.firstMaleVariantId
        ? GENDERS.womens
        : GENDERS.mens
    },

    currentDyPageContext() {
      // Format expected by DY
      // https://support.dynamicyield.com/article/page-context/#Page-Context-for-eCommerce-Sites
      return {
        type: DY_PAGE_TYPES.product,
        lng: this.country,
        data: [this.currentSpreeProduct.sku],
      }
    },

    canScrollToSpecs() {
      return this.isVisible && this.hasFetchedData && this.isSpecsMounted
    },
  },

  watch: {
    hasFetchedData: {
      immediate: true,
      handler(hasFetchedData) {
        if (hasFetchedData) {
          this.updateScrollBarWidth()
          this.setGender(this.gender)
          this.findAndSetMatchingVariantId()
          this.initSpecs()
          this.updateDyPageContext()
          window.dispatchEvent(new CustomEvent('product-page-fetched-data'))
        }
      },
    },

    $route(to) {
      if (!to.params.isVariantClick) {
        // Back or forward button was used
        this.setGender(this.gender)
        this.findAndSetMatchingVariantId()
      }
      this.trackVirtPath()
    },

    currentVariant() {
      this.trackProductView()
      this.handleFitFinderPDPScript(this.isRaffle)
    },
  },

  beforeCreate() {
    registerStores({
      filter: filterStore,
      product: productStore,
      checkout: checkoutStore,
    })
  },

  beforeRouteUpdate(to, from, next) {
    if (to.params.isVariantClick) this.updateDyPageContext()
    if (from.query?.lightbox === 'open') this.openGallery(false)
    if (to.query?.lightbox === 'open') this.openGallery(true)
    next()
  },

  beforeRouteLeave() {
    enableZoom()
  },

  created() {
    this.fetchProductData({ slug: this.$route.params.productGroup })
    this.fetchStoreData()
    this.setMediaGalleryPositionFromDy()
    preloadStickyMenuComponent()
    if (this.isIPhone) disableZoom()

    // Needed for DY AB-Test - can be removed after finishing the test
    On.disableComparisonTool = () => (this.isComparisonToolOnDesktop = false)
  },

  mounted() {
    this.isIframeLayout = document.body.classList.contains('iframe-layout')
  },

  methods: {
    ...mapMutations({
      openGallery: 'product/openGallery',
      updateScrollBarWidth: 'page/updateScrollBarWidth',
      setCurrentVariantId: 'product/setCurrentVariantId',
      setGender: 'product/setGender',
      setMediaGalleryPosition: 'product/setMediaGalleryPosition',
    }),

    ...mapActions({
      fetchProductData: 'product/fetchData',
      fetchStoreData: 'store/fetchData',
      addFlashMessage: 'flash/addMessage',
    }),

    updateDyPageContext() {
      if (this.$dyClient.pageContext.data?.[0] === this.currentSpreeProduct.sku) return

      this.$dyClient.pageContext = this.currentDyPageContext
    },

    async initSpecs() {
      // Product Spec is not required in Contentful - only show Specs section if added to Product Data
      if (isEmpty(this.product.productSpec)) return

      this.shouldShowSpecs = true

      // URL ?specs=<slug>
      const params = this.$route?.query?.specs
      if (params) {
        this.specsSlugs = [this.productGroupSlug, ...params.split(/\s|\+|%20|%2B/)]

        await waitFor(this, () => this.canScrollToSpecs)
        this.$refs.specs.$el.scrollIntoView({ behavior: 'smooth', block: 'start' })
      } else {
        this.specsSlugs = [this.productGroupSlug]
      }
    },

    onSpecsMounted() {
      this.isSpecsMounted = true
    },

    addUrlProtocol(url) {
      return /^\/\//.test(url) ? window.location.protocol + url : url
    },

    findAndSetMatchingVariantId() {
      this.setCurrentVariantId(this.findMatchingVariantId())
    },

    getVariantIdByGender() {
      if (this.selectedGender === GENDERS.unisex) return this.product.variants[0]?.id
      return this.selectedGender === GENDERS.womens
        ? this.firstFemaleVariantId
        : this.firstMaleVariantId
    },

    findMatchingVariantId() {
      if (this.$route.params.colors === undefined) return this.getVariantIdByGender()

      const routeColors = this.$route.params.colors.split('-')
      const matchingVariant = this.product.variants.find(variant => {
        if (variant.gender === this.selectedGender) {
          const variantColors = variant.color.toLowerCase().split(' | ')

          // compare two arrays of colors, if the array is empty it means all the colors are matching.
          return xor(routeColors, variantColors).length === 0
        }
      })
      return matchingVariant?.id || this.getVariantIdByGender()
    },

    trackVirtPath() {
      this.$notifier.trackEvent({
        action: this.$notifier.ACTIONS.virtPath,
        details: {
          virtPagePath: this.$route.path,
          virtPageTitle: document.title,
          dyPageType: DY_PAGE_TYPES.product,
          dyPageData: this.currentVariant?.spreeProduct?.sku.split(),
        },
      })
    },

    trackProductView() {
      if (this.currentVariant?.spreeProduct?.sku) {
        this.$notifier.trackEvent({
          action: this.$notifier.ACTIONS.productView,
          details: { variant: this.currentVariant, currency: document.body.dataset.currency },
        })
      }
    },

    async handleFitFinderPDPScript(isRaffleProduct) {
      if (isRaffleProduct) return

      const fitFinderScriptUrl = '//integrations.fitanalytics.com/shop/onrunning/pdp.js'
      const fitFinderRegExp = new RegExp(fitFinderScriptUrl)

      if (!isScriptAddedToDOM(fitFinderRegExp)) {
        await waitFor(this, () => this.isCookieFunctionalityAccepted)
        await toRetry(() => addScriptToDOM(fitFinderScriptUrl))
      }
    },

    /**
     * Remove DY campaign fetch once A/B test is over
     * @see https://onrunning.atlassian.net/browse/B2C-4489
     * @see https://github.com/onrunning/on/pull/5021/commits/dffa8d7f7fd45b465dfe0d78a919c5fbf5f5084b
     */
    async setMediaGalleryPositionFromDy() {
      const data = await DynamicYieldService.fetchCampaign(['pdp-media-gallery-position'])
      const [campaign] = data?.campaigns || []

      this.setMediaGalleryPosition(campaign?.variation?.position)
      this.mediaGalleryPositionCampaignIds = campaign?.campaignIds
    },
  },
}
</script>

<style scoped lang="scss">
.product-page {
  &__content {
    opacity: 0;
    transition: opacity $transition-time-slow ease-in-out;

    &--visible {
      opacity: 1;
    }
  }

  &__loader {
    position: fixed;
    top: 50%;
    left: 50%;
    width: 60px;
    transform: translate(-50%, -50%);
    z-index: 1;
  }
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity $transition-time-fast;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}
</style>
